/*
 * The drive's frontend module (modules.md §8; S-06, S-12 Appendix B). Everything the shell knows about
 * the drive is in this object: its routes, its reducer for `drive.*` events, what it puts into core's
 * slots, how it binds to the running client (`bind.ts`), its translation namespace and its slice's life
 * on disk. `web/src/modules.ts` is the only file that names it, and switching the module off is
 * deleting that one line.
 *
 * What this file may import is its budget discipline: types, the reducer and the slice, the key
 * bindings, the switcher's provider and lazy screens, and nothing else. It is statically reachable from
 * `main.tsx`, so a value import of a screen or of `./i18n/en.ts` — here or anywhere this file reaches —
 * moves that weight into the entry chunk (`core/modules/entryClosure.test.ts`,
 * `scripts/checkBudgets.ts`).
 *
 * The slots: the drive's own column on its place, the Details column's views (Details, then a file's
 * Versions, then an item's Activity), the administrators' page and its part of *Open to AI chats*, the
 * switcher, the renderer of a name found by Search and the keys; and it provides the address of the
 * trash's own search, which the Search page's deleted items lead to. The rail's Drive place is core's (`core/shell/Rail.tsx`), drawn while
 * the instance runs the drive. Beside them stands the page a document's link address opens for
 * somebody without a session, a visitor's (`visitorPages`).
 */

import { lazy } from "react"
import type { FrontendModule } from "../../core/modules/registry.ts"
import { bindDriveModule } from "./bind.ts"
import { versionsDetailsView } from "./files/versionsView.ts"
import { activityDetailsView } from "./folder/activityView.ts"
import { ADMIN_DRIVE_SEGMENT, isLinkId, keyOfPath, trashSearchPath } from "./navigation.ts"
import { driveHeadingSwitcher, driveQuickSwitcher } from "./quickSwitcher.ts"
import { dehydrateDriveSlice, driveReducer, hydrateDriveSlice, type DriveSlice } from "./reducer.ts"
import { driveRoutes } from "./routes.tsx"
import { AdminScreen } from "./screens.ts"
import { KIND_ITEM } from "./search/kind.ts"
import { driveShortcuts, driveShortcutScopes } from "./shortcuts.ts"
import { DRIVE_FOLD } from "./sidebar/fold.ts"
import { DETAILS_GROUP, DETAILS_VIEW } from "./state/details.ts"

/*
 * The column beside the drive's place (S-01 §3.2): New, the places, the reader's shared drives and
 * the trash. It claims the column on its place and folds beside an open document (`sidebar/fold.ts`).
 * Lazy, because a sidebar section is rendered by a core frame from the first paint.
 */
const DriveSidebar = lazy(async () => ({
	default: (await import("./sidebar/DriveSidebar.tsx")).DriveSidebar,
}))

/**
 * A drive item found by its name, in Search's Drive section (`search/ItemHit.tsx`): a chunk of its own,
 * fetched with the first such hit.
 */
const ItemHit = lazy(async () => ({ default: (await import("./search/ItemHit.tsx")).ItemHit }))

/**
 * What AI chats may do in the drive where somebody said otherwise, as the administrators' page *Open
 * to AI chats* lists it: a chunk of its own, fetched when an administrator opens that page.
 */
const AgentReachDrive = lazy(() => import("./admin/AgentReachDrive.tsx"))

/** Details of an item or a drive in core's details column (S-01 §3.2): a chunk of its own. */
const DetailsView = lazy(async () => ({
	default: (await import("./folder/DetailsView.tsx")).DetailsView,
}))

export const drive: FrontendModule = {
	id: "drive",
	// Resolved at once, with each screen a `lazy()` of its own (./screens.ts says why).
	routes: (parent) => Promise.resolve(driveRoutes(parent)),
	reducer: driveReducer,
	bind: bindDriveModule,
	slots: {
		sidebarSections: [
			{
				id: "drive.nav",
				titleKey: "drive.title",
				order: 10,
				place: "drive",
				sidebar: "own",
				fold: DRIVE_FOLD,
				Rows: DriveSidebar,
			},
		],
		// Details, then the files' Versions and every item's Activity as its tabs (`files/versionsView.ts`,
		// `folder/activityView.ts`).
		detailsViews: [
			{
				id: DETAILS_VIEW,
				titleKey: "drive.item.details",
				tabLabelKey: "drive.details.tab.details",
				group: DETAILS_GROUP,
				order: 10,
				View: DetailsView,
			},
			versionsDetailsView,
			activityDetailsView,
		],
		// Administration → Other shared drives: every space of the instance with its groups and size.
		adminPages: [
			{ path: ADMIN_DRIVE_SEGMENT, titleKey: "drive.nav.otherSharedDrives", Page: AdminScreen },
		],
		// Administration → Open to AI chats: the drive's part, first on the page.
		agentReach: [{ id: "drive.drive", order: 10, Part: AgentReachDrive }],
		// The names this visit knows, then — for two words or more — the headings of those documents.
		quickSwitcherProviders: [driveQuickSwitcher, driveHeadingSwitcher],
		searchResultRenderers: { [KIND_ITEM]: ItemHit },
		shortcuts: driveShortcuts,
		shortcutScopes: driveShortcutScopes,
	},
	// The Search page's *Search the trash* leads to the trash's own search (DS-3 §3.3).
	provides: { trashSearch: trashSearchPath },
	// A document's link address opened by somebody without a session (DV W-1 §3.7): the visitor's
	// page, core's, in a chunk of its own that nobody signed in ever fetches.
	visitorPages: [
		{
			matches: (pathname) => isLinkId(keyOfPath(pathname)),
			load: () => import("../../core/docs/visitor/VisitorPage.tsx").then((chunk) => chunk.default),
		},
	],
	// The copy is a chunk: the router asks for it beside the session and registers it before the first
	// render, so no screen of the drive ever draws a key without its text.
	i18n: () => import("./i18n/en.ts").then((chunk) => chunk.en),
	hydrate: hydrateDriveSlice,
	dehydrate: (slice) => dehydrateDriveSlice(slice as DriveSlice),
}

export default drive
